<template>
  <!-- One photo or video in any saved shape (utils/blocks.js mediaOf), styled
       by the theme's photo treatment. Photos lazy-load; videos never autoplay
       with sound and respect reduced motion. -->
  <figure v-if="m.kind" class="s2-media" :class="{'s2-media--zoom': zoom && m.kind === 'image'}">
    <button v-if="zoom && m.kind === 'image'" type="button" class="s2-media__btn" :aria-label="`See the photo bigger${m.caption ? ': ' + m.caption : ''}`" @click="$emit('zoom')">
      <img :src="m.src" v-bind="imgAttrs(m.src, '(min-width: 1024px) 60vw, 100vw')" :alt="imgAlt(m.src, alt, m.caption)" loading="lazy" decoding="async" class="t-photo s2-media__el" :style="fitStyle" />
    </button>
    <img v-else-if="m.kind === 'image'" :src="m.src" v-bind="imgAttrs(m.src, '(min-width: 1024px) 60vw, 100vw')" :alt="imgAlt(m.src, alt, m.caption)" loading="lazy" decoding="async" class="t-photo s2-media__el" :style="fitStyle" />
    <video v-else class="t-photo s2-media__el" :src="videoSrc" :poster="posterSrc || null" :style="fitStyle"
           :controls="!autoplay" :autoplay="autoplay && !reduced" :muted="autoplay" :loop="autoplay" playsinline preload="metadata" />
    <figcaption v-if="caption && m.caption" class="t-muted s2-media__cap">{{ m.caption }}</figcaption>
  </figure>
</template>

<script setup>
import {useImageAlt} from "@/composables/useImageAlt.js";
const imgAlt = useImageAlt();
import {imgAttrs} from "@/utils/photoSizes.js";
import {computed, onMounted, ref} from "vue";
import {mediaOf} from "@/utils/blocks.js";

const props = defineProps({
  media: {type: [Object, String], default: null},
  alt: {type: String, default: ""},
  caption: {type: Boolean, default: false},
  zoom: {type: Boolean, default: false},
  ratio: {type: String, default: ""},
  autoplay: {type: Boolean, default: false},
});
defineEmits(["zoom"]);
const m = computed(() => mediaOf(props.media));
const fitStyle = computed(() => (props.ratio ? {aspectRatio: props.ratio, objectFit: "cover", width: "100%", height: "auto"} : {}));
const reduced = ref(false);
onMounted(() => { reduced.value = !!window.matchMedia?.("(prefers-reduced-motion: reduce)").matches; });
const proxied = (url) => {
  if (!url || !url.includes("pexels.com/")) return url;
  const prefix = import.meta.env.VITE_IS_PREVIEW_SERVER === "true" ? "/api/preview" : "";
  return `${prefix}/api/proxy/pexels?url=${encodeURIComponent(url)}`;
};
const videoSrc = computed(() => proxied(m.value.src));
const posterSrc = computed(() => m.value.poster);
</script>

<style scoped>
.s2-media { margin: 0; }
.s2-media__btn { display: block; width: 100%; padding: 0; border: 0; background: none; cursor: zoom-in; }
.s2-media__btn:focus-visible { outline: 3px solid color-mix(in srgb, var(--t-accent) 55%, transparent); outline-offset: 3px; }
.s2-media__el { display: block; width: 100%; height: auto; }
.s2-media__cap { font-size: .85rem; margin-top: .45rem; font-style: italic; }
</style>
